import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageSquare, Phone, ChevronRight, Send } from "lucide-react";
import axiosInstance from "../services/BaseUrl";
import { useLocation } from "react-router-dom";
import WhatsAppIcon from "./icons/WhatsAppIcon";

// WhatsApp official colors
const COLORS = {
  primary: "#25D366",
  secondary: "#128C7E",
  dark: "#075E54",
  light: "#DCF8C6",
  accent: "#34B7F1",
  background: "#ECE5DD",
  white: "#FFFFFF",
  gray: "#F5F5F5",
  darkGray: "#4A4A4A"
};

const WhatsAppWidget = ({
  defaultPhoneNumber = "+971552554245",
  defaultMessage = `Hi! I love this project:\nhttps://www.justoffplan.ai${location.pathname}\nCan you share full details and best offer?`,
  companyName = "justoffplan.ai",
  agentName = "JustOffPlan Support",
  position = "right",
  greetingMessage = "Hi there! 👋 How can we assist you today?",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [countryCode, setCountryCode] = useState("+971");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [developerDetails, setDeveloperDetails] = useState(null);

  const location = useLocation();

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!phoneNumber || !/^\d+$/.test(phoneNumber)) {
      setError("Please provide a valid phone number.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axiosInstance.post("enquiry/", {
        countryCode: countryCode,
        phoneNumber: phoneNumber,
        propertyPath: location.pathname
      });

      if (response.data.success) {
        setPropertyDetails(response.data.propertyDetails);
        setDeveloperDetails(response.data.developerDetails);
        setStep(2);
      } else {
        setError(response.data.message || "Failed to submit enquiry.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to connect to server.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMessageSubmit = () => {
    let finalMessage = defaultMessage;
    
    // Add developer details if available (without image URL)
    if (developerDetails) {
      finalMessage += `\n\nDeveloper: ${developerDetails.name}`;
      // Remove the image URL part completely
    }

    const isDesktop = !/Mobi|Android/i.test(navigator.userAgent);
    const whatsappUrl = isDesktop
      ? `https://web.whatsapp.com/send?phone=${defaultPhoneNumber}&text=${encodeURIComponent(
          finalMessage
        )}`
      : `https://wa.me/${defaultPhoneNumber}?text=${encodeURIComponent(
          finalMessage
        )}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    setIsOpen(false);
    setStep(1);
    setPropertyDetails(null);
    setDeveloperDetails(null);
  };

  const countryOptions = [
    { value: "+971", label: "🇦🇪 UAE (+971)" },
    { value: "+966", label: "🇸🇦 KSA (+966)" },
    { value: "+1", label: "🇺🇸 USA (+1)" },
    { value: "+44", label: "🇬🇧 UK (+44)" },
    { value: "+91", label: "🇮🇳 India (+91)" },
  ];

  return (
    <div className="whatsapp-widget-container">
      {/* Floating Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed z-50 flex items-center justify-center w-14 h-14 rounded-full shadow-xl cursor-pointer bg-gradient-to-br from-[${COLORS.primary}] to-[${COLORS.secondary}] hover:from-[${COLORS.secondary}] hover:to-[${COLORS.dark}] transition-all duration-300 ${
          position === "right" ? "right-7" : "left-5"
        } bottom-10`}
        aria-label={isOpen ? "Close WhatsApp chat" : "Open WhatsApp chat"}
        style={{
          background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
        }}
      >
        {isOpen ? (
          <X className="text-white w-6 h-6" />
        ) : (
          <div className="relative">
            <WhatsAppIcon />
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center"
            >
              <div 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: COLORS.accent }}
              />
            </motion.div>
          </div>
        )}
      </motion.button>

      {/* Chat Popup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="WhatsApp Chat"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className={`fixed z-50 flex flex-col rounded-2xl shadow-2xl w-[90vw] sm:w-[350px] max-h-[80vh] sm:max-h-[500px] bg-white ${
              position === "right" ? "right-5" : "left-5"
            } bottom-[90px] overflow-hidden border border-gray-200`}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-4 text-white"
              style={{
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">{companyName}</h2>
                  <p className="text-xs opacity-90 flex items-center gap-1">
                    <span 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: COLORS.accent }}
                    />
                    Online now
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setStep(1);
                  setError(null);
                }}
                className="flex items-center justify-center w-8 h-8 text-white transition-colors rounded-full hover:bg-white/20 focus:outline-none"
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            </div>

            {/* Chat Body */}
            <div 
              className="flex-1 p-4 overflow-y-auto"
              style={{ backgroundColor: COLORS.background }}
            >
              {step === 1 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-4">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: COLORS.light }}
                  >
                    <Phone 
                      className="w-8 h-8" 
                      style={{ color: COLORS.secondary }}
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Start a conversation
                  </h3>

                  <p className="text-sm text-gray-600 mb-6">
                    Enter your WhatsApp number to chat with our support team
                  </p>
                  {error && (
                    <div className="w-full p-3 mb-4 text-sm text-red-600 bg-red-50 rounded-lg">
                      {error}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium mt-1"
                      style={{ 
                        backgroundColor: COLORS.light,
                        color: COLORS.dark
                      }}
                    >
                      {agentName.charAt(0)}
                    </div>
                    <div className="max-w-[80%]">
                      <div 
                        className="inline-block rounded-xl rounded-tl-none p-3 shadow-sm"
                        style={{ backgroundColor: COLORS.white }}
                      >
                        <p className="text-gray-800 text-sm">
                          {greetingMessage}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Just now</p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="max-w-[80%]">
                      <div
                        className="inline-block rounded-xl rounded-tr-none p-3 shadow-sm text-white text-sm"
                        style={{
                          backgroundColor: COLORS.primary,
                          whiteSpace: "normal",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        <p>{defaultMessage}</p>
                        {developerDetails && (
                          <p className="mt-2 text-xs opacity-90">
                            + Developer: {developerDetails.name}
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-right">
                        Ready to send
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-200">
              {step === 1 ? (
                <form onSubmit={handlePhoneSubmit} className="space-y-4">
                  <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="bg-gray-100 text-gray-700 outline-none px-3 py-2 text-sm border-r border-gray-300"
                    >
                      {countryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) =>
                        setPhoneNumber(e.target.value.replace(/\D/g, ""))
                      }
                      placeholder="Phone number"
                      className="flex-1 p-3 text-gray-700 outline-none text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!phoneNumber.trim() || isLoading}
                    className={`w-full py-3 px-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all ${
                      phoneNumber.trim() && !isLoading
                        ? `bg-gradient-to-r from-[${COLORS.primary}] to-[${COLORS.secondary}] hover:from-[${COLORS.secondary}] hover:to-[${COLORS.dark}]`
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                    style={{
                      background: phoneNumber.trim() && !isLoading 
                        ? `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})`
                        : COLORS.gray
                    }}
                    aria-label="Continue to message"
                  >
                    {isLoading ? (
                      "Processing..."
                    ) : (
                      <>
                        Continue <ChevronRight size={18} />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <button
                  onClick={handleMessageSubmit}
                  className="w-full py-3 px-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all"
                  style={{
                    background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})`,
                  }}
                  aria-label="Send message"
                >
                  Send Message <Send size={18} />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WhatsAppWidget;